import {
  SET_ANALYTICS_FILTER_OPTIONS,
  SET_LINE_GLASS_MODEL,
  VAR_ARRAY,
  GLASS_ARRAY
} from '../actionType';

export const setAnalyticsFilterOptions = (options) => (dispatch) => {
  dispatch({
    type: SET_ANALYTICS_FILTER_OPTIONS,
    options,
  });
};

// Funzione helper per serializzare un riferimento Firestore
function serializeRef(ref) {
  if (!ref) return null;
  if (typeof ref === 'string') return { id: ref };

  // Controlla se è un oggetto con proprietà type='document' e firestore (DocumentReference)
  if (ref && typeof ref === 'object' && ref.type === 'document' && ref.firestore) {
    return { id: typeof ref.id === 'string' ? ref.id : String(ref) };
  }

  // Controlla se è un DocumentReference in modo più accurato
  if (ref && typeof ref === 'object' && typeof ref.id === 'string' &&
    (ref.path || ref.parent || ref.firestore)) {
    return { id: ref.id };
  }

  return ref && ref.id ? { id: ref.id } : null;
}

// Funzione helper per serializzare un array di riferimenti
function serializeRefArray(refArray) {
  if (!Array.isArray(refArray)) return [];
  return refArray
    .filter(ref => ref)
    .map(ref => serializeRef(ref))
    .filter(Boolean);
}

export const setLinesProductVariants = (data) => (dispatch) => {
  // Verifica che data contenga effettivamente tutte le proprietà necessarie
  if (!data || typeof data !== 'object') {
    dispatch({
      type: SET_LINE_GLASS_MODEL,
      payload: { lineList: [], glassList: [], modelList: [] },
    });
    return;
  }

  try {
    // Gestisci i nuovi dati dalla collezione AggregateSessions
    if (data.list_products) {
      // Formato nuovo da AggregateSessions
      const lineList = Array.isArray(data.list_lines) ? data.list_lines : [];
      const productList = Array.isArray(data.list_products) ? data.list_products : [];
      const variantList = Array.isArray(data.list_variants) ? data.list_variants : [];

      // Assicuriamoci che i dati dei prodotti siano validi
      const validProducts = productList.filter(product =>
        product && product.id && (product.productName || product.nome_modello)
      );

      const serializedData = {
        lineList: lineList.map(line => ({
          id: line.lineRef,
          nome_linea: line.lineName,
          // Dati aggiuntivi di analytics
          averageEngagementTime: line.averageEngagementTime,
          averageSessionTime: line.averageSessionTime,
          averageSessionsPerUser: line.averageSessionsPerUser,
          newUsersCount: line.newUsersCount,
          numberOfSessions: line.numberOfSessions,
          numberOfUniqueUsers: line.numberOfUniqueUsers,
          visualization: line.visualization,
          time: line.time
        })).filter(Boolean),

        glassList: validProducts.map(product => {
          // Ottieni l'ID del prodotto in modo sicuro
          let productId = product.id;

          // Se product.id è un riferimento a un documento Firestore, estraiamo solo l'ID
          if (productId && typeof productId === 'object' && productId !== null) {
            if (productId.id) {
              productId = productId.id;
            } else if (productId.type === 'document' && productId.firestore) {
              productId = productId.id || '';
            }
          }

          return {
            id: productId,
            nome_modello: product.productName || product.nome_modello,
            lineaRef: { id: product.lineRef },
            // Dati aggiuntivi di analytics
            averageEngagementTime: product.averageEngagementTime,
            averageSessionTime: product.averageSessionTime,
            averageSessionsPerUser: product.averageSessionsPerUser,
            newUsersCount: product.newUsersCount,
            numberOfSessions: product.numberOfSessions,
            numberOfUniqueUsers: product.numberOfUniqueUsers,
            visualization: product.visualization,
            time: product.time
          };
        }).filter(Boolean),

        modelList: variantList.map(variant => {
          let variantId = variant.variantRef;
          let productId = variant.productRef;

          // Serializza l'ID della variante
          if (variantId && typeof variantId === 'object' && variantId !== null) {
            if (variantId.id) {
              variantId = variantId.id;
            } else if (variantId.type === 'document' && variantId.firestore) {
              variantId = variantId.id || '';
            }
          }

          // Serializza l'ID del prodotto
          if (productId && typeof productId === 'object' && productId !== null) {
            if (productId.id) {
              productId = productId.id;
            } else if (productId.type === 'document' && productId.firestore) {
              productId = productId.id || '';
            }
          }

          return {
            id: variantId,
            glassId: productId,
            lineRef: variant.lineRef,
            sizeRef: variant.sizeRef,
            esaColorFramePrimary: variant.esaColorFramePrimary,
            esaColorFrameSecondary: variant.esaColorFrameSecondary,
            esaColorLensesPrimary: variant.esaColorLensesPrimary,
            esaColorLensesSecondary: variant.esaColorLensesSecondary,
            // Dati aggiuntivi di analytics
            averageEngagementTime: variant.averageEngagementTime,
            averageSessionTime: variant.averageSessionTime,
            averageSessionsPerUser: variant.averageSessionsPerUser,
            newUsersCount: variant.newUsersCount,
            numberOfSessions: variant.numberOfSessions,
            numberOfUniqueUsers: variant.numberOfUniqueUsers,
            visualization: variant.visualization,
            time: variant.time
          };
        }).filter(Boolean)
      };

      dispatch({
        type: SET_LINE_GLASS_MODEL,
        payload: serializedData,
      });
      return;
    }

    // Assicurati che lineList, glassList e modelList siano array (retrocompatibilità)
    const lineList = Array.isArray(data.lineList) ? data.lineList : [];
    const glassList = Array.isArray(data.glassList) ? data.glassList : [];
    const modelList = Array.isArray(data.modelList) ? data.modelList : [];

    // Serializza i dati per garantire la compatibilità con redux (per il formato precedente)
    const serializedData = {
      lineList: lineList.map(line => {
        if (!line || typeof line !== 'object') return null;

        // Supporta sia il vecchio formato che quello nuovo
        const lineData = {
          ...line,
          id: line.id || line.idLinea || line.lineRef,
          nome_linea: line.nome_linea || line.name || line.lineName,
          brand: serializeRef(line.brand),
          listaRefsOcchiale: serializeRefArray(line.listaRefsOcchiale || [])
        };

        return lineData;
      }).filter(Boolean), // Rimuove i null

      glassList: glassList.map(glass => {
        if (!glass || typeof glass !== 'object') return null;

        // Ottieni l'ID del prodotto in modo sicuro
        let glassId = glass.id || glass.idOcchiale || glass.mainProduct || glass.productRef;

        // Se glassId è un riferimento a un documento Firestore, estraiamo solo l'ID
        if (glassId && typeof glassId === 'object' && glassId !== null) {
          if (glassId.id) {
            glassId = glassId.id;
          } else if (glassId.type === 'document' && glassId.firestore) {
            glassId = glassId.id || '';
          }
        }

        // Supporta sia il vecchio formato che quello nuovo
        const glassData = {
          ...glass,
          id: glassId,
          nome_modello: glass.nome_modello || glass.modelName || glass.glassesName || glass.productName,
          brand: serializeRef(glass.brand),
          lineaRef: serializeRef(glass.lineaRef) ||
            (glass.list_line_tags && glass.list_line_tags.length > 0 ?
              serializeRef({ id: glass.list_line_tags[0] }) :
              glass.lineRef ? serializeRef({ id: glass.lineRef }) : null),
          lista_categorie: serializeRefArray(glass.lista_categorie || []),
          lista_taglie: serializeRefArray(glass.lista_taglie || glass.list_variants?.map(id => ({ id })) || [])
        };

        // Gestiamo logLoadingModel separatamente per evitare problemi di serializzazione
        if (glass.logLoadingModel) {
          if (typeof glass.logLoadingModel === 'string') {
            glassData.logLoadingModel = { id: glass.logLoadingModel };
          } else if (typeof glass.logLoadingModel === 'object' && glass.logLoadingModel !== null) {
            // Per sicurezza salviamo solo l'ID
            const logId = typeof glass.logLoadingModel.id === 'string' ?
              glass.logLoadingModel.id :
              (glass.logLoadingModel.toString ? String(glass.logLoadingModel) : '');
            glassData.logLoadingModel = { id: logId };
          }
        }

        return glassData;
      }).filter(Boolean), // Rimuove i null

      modelList: modelList.map(model => {
        if (!model || typeof model !== 'object') return null;

        // Ottieni gli ID in modo sicuro
        let modelId = model.id || model.idModello || model.variantRef;
        let productId = model.glassId || model.productRef;

        // Serializza l'ID del modello
        if (modelId && typeof modelId === 'object' && modelId !== null) {
          if (modelId.id) {
            modelId = modelId.id;
          } else if (modelId.type === 'document' && modelId.firestore) {
            modelId = modelId.id || '';
          }
        }

        // Serializza l'ID del prodotto
        if (productId && typeof productId === 'object' && productId !== null) {
          if (productId.id) {
            productId = productId.id;
          } else if (productId.type === 'document' && productId.firestore) {
            productId = productId.id || '';
          }
        }

        // Supporta sia il vecchio formato che quello nuovo
        const modelData = {
          ...model,
          id: modelId,
          nomeOcchiale: model.nomeOcchiale || model.variantName || model.glassesName,
          glassId: productId,
          brand: serializeRef(model.brand),
          esaColorFramePrimary: model.esaColorFramePrimary || model.colorFramePrimary || model.primaryFrameColorHex,
          esaColorFrameSecondary: model.esaColorFrameSecondary || model.colorFrameSecondary || model.secondaryFrameColorHex,
          esaColorLensesPrimary: model.esaColorLensesPrimary || model.colorLensesPrimary || model.primaryLensesColorHex,
          esaColorLensesSecondary: model.esaColorLensesSecondary || model.colorLensesSecondary || model.secondaryLensesColorHex
        };

        return modelData;
      }).filter(Boolean), // Rimuove i null
    };

    dispatch({
      type: SET_LINE_GLASS_MODEL,
      payload: serializedData,
    });
  } catch (error) {
    dispatch({
      type: SET_LINE_GLASS_MODEL,
      payload: { lineList: [], glassList: [], modelList: [] },
    });
  }
};

export const setGlassArr = (data) => (dispatch) => {
  dispatch({
    type: GLASS_ARRAY,
    payload: data,
  });
};

export const setVarArr = (data) => (dispatch) => {
  dispatch({
    type: VAR_ARRAY,
    payload: data,
  });
};